import {Component} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {AuthUtils} from '../../../utils/AuthUtils';
import {PermissionTargetEnum} from '../../../enums/PermissionTargetEnum';
import {SelectItem} from '../../../dtos/SelectItem';
import {FirmwareService} from '../../../services/rest/firmware.service';
import {InputButtonIconType} from '../../core/button/button.utils';
import {ModuleTypeService} from '../../../services/rest/module-type.service';
import {ChipTypeService} from '../../../services/rest/chip-type.service';
import {DisplayTypeService} from '../../../services/rest/display-type.service';

@Component({
  selector: 'app-firmware-edit',
  templateUrl: './firmware-edit.component.html',
  styleUrls: ['./firmware-edit.component.scss']
})
export class FirmwareEditComponent {
  userCanEdit: boolean = AuthUtils.canEdit(PermissionTargetEnum.FIRMWARE);
  InputButtonIconType = InputButtonIconType;

  softwareTypeOptions: SelectItem[] = [
    { id: 'INTERNAL', name: 'INTERNAL', description: '' },
    { id: 'INTEGRATION', name: 'INTEGRATION', description: '' },
    { id: 'PRODUCTION', name: 'PRODUCTION', description: '' },
    { id: 'BOOTLOADER', name: 'BOOTLOADER', description: '' },
    { id: 'PARTITIONS', name: 'PARTITIONS', description: '' },
    { id: 'OTA_DATA', name: 'OTA_DATA', description: '' }
  ];

  moduleTypeOptions: SelectItem[] = [];
  chipTypeOptions: SelectItem[] = [];
  displayTypeOptions: SelectItem[] = [];

  id!: number;
  form: FormGroup;

  downloadUrl: string | null = null;
  loading = true;
  error: string | null = null;

  constructor(private route: ActivatedRoute,
              private router: Router,
              private service: FirmwareService,
              private moduleTypeService: ModuleTypeService,
              private chipTypeService: ChipTypeService,
              private displayTypeService: DisplayTypeService,
              private fb: FormBuilder) {
    this.moduleTypeService.getAll().subscribe(options => {
      this.moduleTypeOptions = options.map(m => ({
        id: m.code as any,
        name: m.name,
        description: m.code
      }));
    });
    this.chipTypeService.getAll().subscribe(options => {
      this.chipTypeOptions = options.map(c => ({
        id: c.code as any,
        name: c.name,
        description: c.code
      }));
    });
    this.displayTypeService.getAll().subscribe(options => {
      this.displayTypeOptions = options.map(d => ({
        id: d.code as any,
        name: d.name,
        description: d.code
      }));
    });
    this.id = Number(this.route.snapshot.paramMap.get('id'));
    this.form = this.fb.group({
      softwareType: ['', Validators.required],
      moduleTypeCode: ['', Validators.required],
      chipTypeCode: ['', Validators.required],
      displayTypeCode: ['', Validators.required],
      version: [{value: 0, disabled: true}],
      isInitializer: [false],
      file: [null]
    });

    this.form.get('isInitializer')?.valueChanges.subscribe(val => {
      if (val) {
        this.form.get('version')?.setValue(-1);
      }
    });

    this.load();
  }

  load() {
    this.loading = true;
    this.service.getById(this.id).subscribe(res => {
      const softwareType = res.softwareType || '';
      const moduleTypeCode = res.moduleTypeCode || '';
      const chipTypeCode = res.chipTypeCode || '';
      const displayTypeCode = res.displayTypeCode || '';
      const isInitializer = !!res.isInitializer;
      const v = res.version;
      const version = (v === null || v === undefined || v === '') ? 0 : Number(v);
      this.form.patchValue({ softwareType, moduleTypeCode, chipTypeCode, displayTypeCode, version, isInitializer });
      this.downloadUrl = res.downloadUrl;
      this.loading = false;
    }, err => {
      this.error = 'Nicht gefunden';
      this.loading = false;
    });
  }

  onFileSelected(event: any) {
    const f = (event.target?.files && event.target.files.length) ? event.target.files[0] : null;
    this.form.patchValue({ file: f });
  }

  save() {
    if (!this.userCanEdit) return;
    if (!this.form || this.form.invalid) {
      this.form?.markAllAsTouched();
      this.error = 'Bitte Modul angeben';
      return;
    }
    this.error = null;
    const softwareType = this.form.value.softwareType as string;
    const moduleTypeCode = this.form.value.moduleTypeCode as string;
    const chipTypeCode = this.form.value.chipTypeCode as string;
    const displayTypeCode = this.form.value.displayTypeCode as string;
    const isInitializer = !!this.form.value.isInitializer;
    const file = this.form.value.file as File | null | undefined;
    this.service.update(this.id, softwareType, moduleTypeCode, chipTypeCode, displayTypeCode, isInitializer, file || undefined).subscribe(() => {
      this.router.navigate(['firmware']);
    }, err => {
      this.error = (err?.error && err.error.message) ? err.error.message : (err?.error || 'Fehler beim Speichern');
    });
  }

  onDelete() {
    if (!this.userCanEdit) return;
    if (!confirm('Wirklich löschen?')) return;
    this.service.delete(this.id).subscribe(() => this.router.navigate(['firmware']));
  }

  cancel() {
    this.router.navigate(['firmware']);
  }
}
